// Bare-body subscriber-dealers list for the Manufacturing → Plants
// dealers-attached box. Lists the app-using subscriber-org dealers
// attached to the selected plant (or the Unattached pseudo-row's
// set when that's active).
//
// Slice 8: no longer renders its own SectionCard — the parent
// (ManufacturingPlantDealersAttachedBox) collapsed the old outer
// SectionCard + ManufacturingTabbedSection + inner SectionCard
// triple-nest into a single tabbed section, and this body now sits
// directly inside it. The list count moved into the tab label;
// description / title chrome belongs to the parent.
//
// Operations:
//   - Attached view (plant selected): Detach (sets primary_plant_id
//     = null). Attach is owned by the picker in the box header.
//   - Unattached view: a per-row Set primary plant dropdown +
//     "Set" button (slice 8) so users can resolve "no primary plant"
//     alerts in-place without opening a separate flow. Uses the
//     same setManufacturingDealerPrimaryPlant endpoint as the
//     attach-via-picker path.
import { useState } from "react";
import { ghostBtnClass, mfgPrimaryBtnClass, mfgXsInputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgSubscriberDealer } from "@/lib/manufacturing/dealerTypes";
import type { UseManufacturingPlantDealersTab } from "./useManufacturingPlantDealersTab";

function SetPrimaryPlantInline({
  m,
  sub,
}: {
  m: UseManufacturingPlantDealersTab;
  sub: MfgSubscriberDealer;
}) {
  const [pendingPlantId, setPendingPlantId] = useState<string>("");
  const activePlants = m.plants.filter((p) => p.is_active);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="sr-only" htmlFor={`set-primary-${sub.dealer_org_id}`}>
        Set primary plant
      </label>
      <select
        id={`set-primary-${sub.dealer_org_id}`}
        className={mfgXsInputClass}
        value={pendingPlantId}
        onChange={(e) => setPendingPlantId(e.target.value)}
        disabled={m.actionBusy || activePlants.length === 0}
      >
        <option value="">Set primary plant…</option>
        {activePlants.map((p) => (
          <option key={p.id} value={p.id}>
            {p.plant_code} · {p.name}
            {p.is_default ? " (default)" : ""}
          </option>
        ))}
      </select>
      <button
        type="button"
        className={mfgPrimaryBtnClass}
        onClick={() => {
          if (!pendingPlantId) return;
          void m.assignSubscriberToPlant(sub, pendingPlantId).then(() => {
            setPendingPlantId("");
          });
        }}
        disabled={m.actionBusy || !pendingPlantId}
      >
        Set
      </button>
    </div>
  );
}

export function ManufacturingPlantDealersSubscriberSection({
  m,
}: {
  m: UseManufacturingPlantDealersTab;
}) {
  const list = m.scopedSubscribers;
  return (
    <div className="space-y-1">
      {m.listBusy ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">Loading dealers…</div>
      ) : null}
      {!m.listBusy && list.length === 0 ? (
        <div className="text-sm text-gray-500 dark:text-zinc-400">
          {m.isUnattached
            ? "No unattached subscriber dealers — every app-using dealer in this org has a primary plant set."
            : "No app-using dealers attached to this plant yet. Use the Attach dealer affordance in the header."}
        </div>
      ) : null}
      <ul className="divide-y divide-gray-100 dark:divide-zinc-700">
        {list.map((sub) => (
          <li key={sub.dealer_org_id} className="py-3">
            <div className="flex flex-wrap items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                  {sub.dealer_org_name}
                  {!sub.is_active ? (
                    <span className="ml-2 rounded-md bg-gray-400 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                      Inactive
                    </span>
                  ) : null}
                </div>
                {sub.notes ? (
                  <div className="mt-1 text-xs text-gray-600 italic dark:text-zinc-400">
                    {sub.notes}
                  </div>
                ) : null}
                <label className="mt-2 flex items-center gap-2 text-xs text-gray-700 dark:text-zinc-300">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 dark:border-zinc-600"
                    checked={sub.service_resale_enabled}
                    onChange={(e) => void m.setSubscriberServiceResale(sub, e.target.checked)}
                    disabled={m.actionBusy || !sub.is_active}
                    title="When enabled, this subscriber can see your quotable service offerings in their sales lanes"
                  />
                  Service resale
                </label>
              </div>
              {m.isUnattached ? (
                <SetPrimaryPlantInline m={m} sub={sub} />
              ) : (
                <button
                  type="button"
                  className={ghostBtnClass}
                  onClick={() => void m.detachSubscriber(sub)}
                  disabled={m.actionBusy}
                  title="Clear this dealer's primary plant (re-attach later via a plant's Attach affordance)"
                >
                  Detach
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
